import type { PublicDocument } from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';
import { ActionLink, type ActionLinkSize, type ActionLinkVariant } from './ActionLink';

export interface DocumentLinkProps {
    document: PublicDocument;
    /** Haalt de PDF zelf op; `false`: de browser volgt de link. */
    onOpenDocument?: ((document: PublicDocument) => boolean | void) | undefined;
    /** De PDF van deze knop komt nu binnen. */
    busy: boolean;
    variant: ActionLinkVariant;
    size: ActionLinkSize;
    copy: PublicCopy;
    className?: string;
}

/**
 * Een downloadknop voor een document van de pagina. Een echte link (nieuw
 * tabblad en "opslaan als" werken), maar een gewone tik haalt de PDF op via de
 * pagina, zodat een PDF die er nog niet is een rustige zin oplevert en geen
 * scherm vol foutcode. Terwijl hij komt, zegt de knop dat.
 */
export function DocumentLink({
    document,
    onOpenDocument,
    busy,
    variant,
    size,
    copy,
    className,
}: DocumentLinkProps) {
    return (
        <ActionLink
            href={document.url}
            onClick={
                onOpenDocument
                    ? (event) => {
                          /* Met een toets erbij (nieuw tabblad, opslaan) doet de browser het zelf. */
                          if (
                              event.button !== 0 ||
                              event.metaKey ||
                              event.ctrlKey ||
                              event.shiftKey ||
                              event.altKey
                          ) {
                              return;
                          }

                          if (onOpenDocument(document) !== false) {
                              event.preventDefault();
                          }
                      }
                    : undefined
            }
            aria-busy={busy || undefined}
            size={size}
            variant={variant}
            className={cn('max-w-full', className)}
        >
            {busy
                ? copy.pdfBusy
                : document.detail
                  ? `${document.label} · ${document.detail}`
                  : document.label}
        </ActionLink>
    );
}

/** Onder de knoppen als de PDF niet kwam: amber en kalm, nooit rood. */
export function DocumentUnavailable({ copy }: { copy: PublicCopy }) {
    return (
        <p
            role="alert"
            className="m-0 mt-3 rounded-[8px] border border-[var(--warn-border)] bg-[var(--warn-tint-2)] px-3 py-[9px] text-[13px] leading-[1.45] text-pretty text-[var(--warn-text)]"
        >
            {copy.pdfUnavailable}
        </p>
    );
}
